import { GoogleGenAI, GenerateContentResponse } from "@google/genai";
import { GEMINI_MODEL_NAME } from '../constants';

// Load environment variables (make sure VITE_GEMINI_API_KEY is set in your .env file)
const API_KEY = import.meta.env.VITE_GEMINI_API_KEY;

// Check if API key is available at module load time
if (!API_KEY) {
  // This will log during development when the module is imported
  console.error("Gemini API key (VITE_GEMINI_API_KEY) not found in environment variables. The app may not function correctly.");
}

let ai: GoogleGenAI | null = null;

if (API_KEY) {
  ai = new GoogleGenAI({ apiKey: API_KEY });
}

export const extractPluginNamesFromText = async (text: string): Promise<string[]> => {
  if (!ai) {
    throw new Error("Gemini API client is not initialized. API_KEY might be missing.");
  }

  try {
    const prompt = `Your task is to extract plugin names from the provided text, which is a copy-paste from a WordPress admin plugins page.
The output should be a JSON array of strings, where each string is a plugin name.
Focus on the main title of each plugin. Ignore version numbers, author names, descriptions, action links (like "Deactivate", "Settings"), "Select" prefixes, and any other surrounding text.
Do not include duplicates. Only list each unique plugin name once. Sort the plugin names alphabetically.

Example Input Snippet:
"Select Advanced Custom Fields PRO
	
Advanced Custom Fields PRO
Deactivate | WPTC Rollback
	

Customize WordPress with powerful, professional and intuitive fields.
Version 6.4.2 | By WP Engine | Visit plugin site"

Expected output for this snippet (alphabetical): ["Advanced Custom Fields PRO"]

Input Text:
---
${text}
---
`;

    const response: GenerateContentResponse = await ai.models.generateContent({
      model: GEMINI_MODEL_NAME,
      contents: prompt,
      config: {
        responseMimeType: "application/json",
        temperature: 0.1, // Lower temperature for more deterministic output
      },
    });
    
    if (!response.text) {
      throw new Error('No response text received from Gemini API');
    }
    
    let jsonString = response.text.trim();
    const fenceRegex = /^```(?:json)?\s*\n?(.*?)\n?\s*```$/si; // Case-insensitive, optional "json"
    const match = jsonString.match(fenceRegex);
    if (match && match[1]) {
      jsonString = match[1].trim();
    }

    const parsed = JSON.parse(jsonString);
    if (Array.isArray(parsed) && parsed.every(item => typeof item === 'string')) {
      // Ensure uniqueness and sort alphabetically as a fallback, though prompt asks for it
      const uniquePlugins = Array.from(new Set(parsed as string[]));
      return uniquePlugins.sort((a,b) => a.localeCompare(b));
    } else {
      console.error("Gemini response was not a valid JSON array of strings:", parsed);
      throw new Error("Failed to parse plugin names from Gemini response. Expected a JSON array of strings.");
    }

  } catch (error) {
    console.error("Error calling Gemini API:", error);
    if (error instanceof Error) {
        throw new Error(`Failed to extract plugin names: ${error.message}`);
    }
    throw new Error("Failed to extract plugin names due to an unknown error.");
  }
};

import { PluginInfo } from '../types';

export const getPluginDetails = async (pluginName: string): Promise<PluginInfo> => {
  if (!ai) {
    throw new Error("Gemini API client is not initialized. API_KEY might be missing.");
  }

  const prompt = `
    You are an AI assistant. Your task is to provide detailed information about a specific WordPress plugin.
    The plugin name is: "${pluginName}".

    Please provide the following information in a valid JSON object format:
    - name: The official or commonly recognized name of the plugin. This should ideally match or be very close to "${pluginName}".
    - description: A concise summary of what the plugin does.
    - author: The author, company, or primary developer(s) of the plugin.
    - version: The latest stable version number of the plugin (e.g., "1.2.3").
    - requires_wp: The minimum WordPress version required to use the plugin (e.g., "5.0").
    - requires_php: The minimum PHP version required (e.g., "7.4").
    - homepage_url: The official website, WordPress.org plugin page, or repository URL for the plugin.

    If any piece of information is not available or not applicable, use null or an empty string for its value.
    Ensure the output is ONLY the JSON object, without any surrounding text or markdown.

    Example for a plugin like "Akismet Anti-Spam":
    {
      "name": "Akismet Anti-Spam: Spam Protection",
      "description": "Akismet checks your comments and contact form submissions against our global database of spam to prevent your site from publishing malicious content. You can review the comment spam it catches on your blog's Comments admin screen.",
      "author": "Automattic - Anti Spam Team",
      "version": "5.3.2",
      "requires_wp": "5.0",
      "requires_php": "7.0",
      "homepage_url": "https://akismet.com/"
    }

    Now, provide this information for the plugin: "${pluginName}"
  `;

  try {
    const response: GenerateContentResponse = await ai.models.generateContent({
      model: GEMINI_MODEL_NAME,
      contents: prompt, // Pass the prompt string directly as per existing function
      config: { // Use 'config' as per existing function
        responseMimeType: "application/json",
        temperature: 0.3, // Slightly higher for potentially more descriptive content, but still factual
      },
    });

    if (!response.text) {
      throw new Error('No response text received from Gemini API');
    }

    let jsonString = response.text.trim();

    // Remove JSON code fences if present
    const fenceRegex = /^```(?:json)?\s*\n?(.*?)\n?\s*```$/si;
    const match = jsonString.match(fenceRegex);
    if (match && match[1]) {
      jsonString = match[1].trim();
    }

    const pluginInfo: PluginInfo = JSON.parse(jsonString);

    // Basic validation: ensure the name is present.
    // If name is missing from response but other data exists, fill it from input as a fallback.
    if (!pluginInfo.name && Object.keys(pluginInfo).length > 0) {
        pluginInfo.name = pluginName;
    } else if (!pluginInfo.name) {
        // If name is missing and no other data, it's a bad response
        console.warn(`Gemini response for "${pluginName}" was missing a name and other details. Received:`, jsonString);
        throw new Error("Failed to parse valid plugin name from Gemini response.");
    }

    // Ensure all expected optional fields are at least present (as undefined or null if not sent by Gemini)
    // This helps maintain a consistent object shape, though JSON.parse will handle missing fields by not including them.
    // The PluginInfo interface already defines them as optional, so this step is more for ensuring defaults if needed,
    // but usually, if Gemini omits them, they'll be undefined, which is fine.

    return pluginInfo;

  } catch (error) {
    console.error(`Error fetching details for plugin "${pluginName}" from Gemini API:`, error);
    if (error instanceof Error) {
      // Check for specific Gemini errors if possible, e.g., blocked content
      if (error.message.includes("Candidate was blocked due to SAFETY")) {
         throw new Error(`Could not fetch details for "${pluginName}": Content blocked by safety filter. Plugin name might be problematic or trigger filters.`);
      }
      throw new Error(`Failed to get plugin details for "${pluginName}": ${error.message}`);
    }
    throw new Error(`Failed to get plugin details for "${pluginName}" due to an unknown error.`);
  }
};
